import { Field } from "../../../models/Fields";
import FormRow from "../layout/FormRow";
import CustomRadioGroup from "./Radio/CustomRadioGroup";
import { FieldGroup } from "../../../models/FieldGroups";
import CustomDatePicker from "./DateTime/CustomDatePicker";
import ValueSlider from "./Slider/ValueSlider";
import CustomSelect from "./Select/CustomSelect";
import CustomNumberInput from "./Text/CustomNumberInput";
import CustomCheckbox from "./Checkbox/CustomCheckbox";
import CustomCheckboxGroup from "./Checkbox/CustomCheckboxGroup";
import CustomTextInput from "./Text/CustomTextInput";
import { FieldAlias, FieldId, FieldTypeId } from "../../../lib/enums";
import HiddenValueCalculator from "./Hidden/HiddenValueCalculator";

export type InputField = (
  field: Field,
  index: number
) => React.ReactNode | null;

const getElementByType = (field: Field) => {
  switch (field.typeId) {
    case FieldTypeId.TEXT:
      return CustomTextInput;
    case FieldTypeId.DECIMAL:
    case FieldTypeId.INTEGER:
      if (field.alias.includes("VAS")) {
        return ValueSlider;
      } else if (
        field.alias.includes("NumberOfDays") ||
        field.alias.includes("NbrOfDays")
      ) {
        return HiddenValueCalculator;
      }
      return CustomNumberInput;
    case FieldTypeId.DATE:
      return CustomDatePicker;
    case FieldTypeId.CHECKBOX:
      return CustomCheckbox;
    case FieldTypeId.MULTISELECT:
    case FieldTypeId.CHECKBOX_LIST:
      return CustomCheckboxGroup;
    case FieldTypeId.DROPLIST:
      if (field.alias.includes("C_F_D_Diary_")) {
        return CustomCheckbox;
      }
      return CustomSelect;
    case FieldTypeId.MULTILINE_TEXT:
      return CustomTextInput;
    case FieldTypeId.RADIO_BUTTON_LIST:
      return CustomRadioGroup;
    default:
      return null;
  }
};

export const getFieldInputs = (
  fields: Field[],
  fieldGroup?: FieldGroup
): React.ReactNode => {
  const fieldInputById: Record<number, React.ReactNode> = {};
  let fieldsArray: React.ReactNode[] = [];

  fields.forEach((field, index) => {
    const InputElement = getElementByType(field);
    let element;
    if (InputElement) {
      let validations = field.validations || [];
      let required = field.required;
      let value1 = undefined;
      let value2 = undefined;
      let header = field.header;
      if (field.typeId === FieldTypeId.DROPLIST) {
        validations = [...validations, { isNonNegative: field.alias }];
        switch (field.alias) {
          case FieldAlias.D_PERGAMMA_2_DRUG:
            validations = [
              ...validations,
              { isNotNullOrEmpty: FieldAlias.D_PERGAMMA_2_DRUG },
            ];
            required = true;
            break;
          case FieldAlias.D_SJUKSKRIVNING_3_INFECTIONRELATED:
            validations = [
              ...validations,
              {
                isNotNullOrEmpty: FieldAlias.D_SJUKSKRIVNING_3_INFECTIONRELATED,
              },
            ];
            required = true;
            break;
          case FieldAlias.D_SJUKSKRIVNING_4_WORKING:
            validations = [
              ...validations,
              { isNotNullOrEmpty: FieldAlias.D_SJUKSKRIVNING_4_WORKING },
            ];
            required = true;
            break;
          case FieldAlias.D_SJUKSKRIVNING_5_WORKINGPERCENT:
            validations = [
              ...validations,
              {
                isNotNullOrEmpty: FieldAlias.D_SJUKSKRIVNING_5_WORKINGPERCENT,
              },
            ];
            required = true;
            break;
          default:
            break;
        }
      } else if (field.typeId === FieldTypeId.DATE) {
        validations = [...validations, { isNotInFuture: field.alias }];
        switch (field.alias) {
          case FieldAlias.D_SJUKSKRIVNING_1_START_DATUM:
            validations = [
              ...validations,
              { isBefore: FieldAlias.D_SJUKSKRIVNING_2_SLUT_DATUM },
            ];
            required = true;
            break;
          case FieldAlias.D_CAREDAYS_1_DATESTART:
            validations = [
              ...validations,
              { isBefore: FieldAlias.D_CAREDAYS_2_DATEEND },
            ];
            required = true;
            break;
          default:
            break;
        }
      } else if (field.typeId === FieldTypeId.CHECKBOX_LIST) {
        switch (field.alias) {
          case FieldAlias.D_PERGAMMA_5_ADVERSE_EVENT:
            break;
          default:
            break;
        }
      } else if (
        field.typeId === FieldTypeId.INTEGER ||
        field.typeId === FieldTypeId.DECIMAL
      ) {
        switch (field.alias) {
          case FieldAlias.D_PERGAMMA_3_DOSAGE:
            validations = [
              ...validations,
              { isNonNegative: FieldAlias.D_PERGAMMA_3_DOSAGE },
            ];
            required = true;
            header = `${field.header} (ml)`;
            break;
          case FieldAlias.D_SJUKSKRIVNING_NUMBEROFDAYS:
            value1 = FieldAlias.D_SJUKSKRIVNING_1_START_DATUM;
            value2 = FieldAlias.D_SJUKSKRIVNING_2_SLUT_DATUM;
            break;
          case FieldAlias.C_D_CAREDAYS_NUMBER_OF_DAYS:
            value1 = FieldAlias.D_CAREDAYS_1_DATESTART;
            value2 = FieldAlias.D_CAREDAYS_2_DATEEND;
            break;
          default:
            break;
        }
      }

      element = (
        <InputElement
          key={`${field.alias}-${field.fieldId}-${field.typeId}-${index}`}
          fieldId={field.fieldId}
          alias={field.alias}
          header={header}
          typeId={field.typeId}
          fieldOptions={(field.fieldOptions as any) || null}
          fieldOption={(field.fieldOptions?.[0] as any) || null}
          valueProp={"id"}
          labelProp={"name"}
          required={required}
          validations={validations}
          value1={value1}
          value2={value2}
        />
      );

      element = (
        <FormRow gapBefore gapAfter key={`${field.alias}`}>
          {element}
        </FormRow>
      );

      fieldInputById[field.fieldId] = element;
    }
  });

  fieldGroup?.fieldGroupFields
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((a) => a.fieldId !== FieldId.C_D_PERGAMMA_DOSAGE)
    .forEach((fieldGroupField) => {
      const field = fields.find((a) => a.fieldId === fieldGroupField.fieldId);
      if (!field) {
        throw new Error(
          `No field found for fieldId: ${fieldGroupField.fieldId}`
        );
      }
      const fieldInput = fieldInputById[field.fieldId];
      if (!fieldInput) {
        throw new Error(`No input element found for alias: ${field.alias}`);
      }
      if (!fieldInput) {
        throw new Error(
          `Input element for alias: ${field.alias} returned null`
        );
      }
      fieldsArray.push(fieldInput);
    });

  return <>{fieldsArray.map((element) => element)}</>;
};

export const getFieldInputForNonFormPage = (
  fields: Field[],
  fieldAlias: string
): React.ReactNode => {
  const fieldInputByAlias: Record<string, React.ReactNode> = {};

  fields.forEach((field, index) => {
    const InputElement = getElementByType(field);
    let element;

    if (InputElement) {
      let validations = field.validations || [];
      let value1 = undefined;
      let value2 = undefined;
      if (field.typeId === FieldTypeId.DROPLIST) {
        validations = [...validations, { isNonNegative: field.alias }];
      }

      element = (
        <InputElement
          key={`${field.alias}-${field.fieldId}-${field.typeId}-${index}`}
          fieldId={field.fieldId}
          alias={field.alias}
          header={field.header}
          typeId={field.typeId}
          fieldOptions={(field.fieldOptions as any) || null}
          fieldOption={(field.fieldOptions?.[0] as any) || null}
          valueProp={"id"}
          labelProp={"name"}
          required={field.required}
          nonForm={true}
          validations={validations}
          value1={value1}
          value2={value2}
        />
      );

      if (
        field.typeId === FieldTypeId.RADIO_BUTTON_LIST ||
        field.typeId === FieldTypeId.CHECKBOX_LIST
      ) {
        element = (
          <FormRow gapBefore gapAfter key={`${field.alias}`}>
            {element}
          </FormRow>
        );
      }

      fieldInputByAlias[field.alias] = element;
    }
  });

  return fieldInputByAlias[fieldAlias];
};
